<script lang="ts">
  import { _ } from 'svelte-i18n';

  import { LEGAL_FORM_CODES } from '$lib/filters/legalforms';
  import { excludeLegalForm, legalForm } from '$lib/stores/selection';

  /**
   * Every legal form the register numbers, each one kept or dropped.
   *
   * # One control where there were two
   *
   * This was a dropdown offering three forms — AG, GmbH, Einzelfirma — behind a
   * checkbox that turned the whole filter on, plus a separate switch that
   * removed sole proprietorships and partnerships together. Two controls
   * writing the same pair of fields, neither of which could say "only
   * Genossenschaften" or "anything but a Zweigniederlassung".
   *
   * The register numbers eighteen forms and the corpus holds sixteen of them.
   * Offering three of those and a preset for two more was an answer to the
   * common case that had no way to express the rest.
   *
   * # Keep and drop are different columns, not one tri-state
   *
   * They are two fields on the wire — `legal_form` is an IN, `exclude_legal_form`
   * is a NOT IN — and a customer setting both is meaningful: "AGs, but no
   * branches of foreign companies". A single control cycling through three
   * states would hide that, and a cycling row gives no hint of what the next
   * click does.
   *
   * A row can be in one column or the other, never both, because a form cannot
   * be simultaneously the only thing kept and a thing removed. Ticking one side
   * clears the other rather than refusing the click.
   */

  let {
    /**
     * Whether to draw the heading. False in the guided flow, where ScreenView
     * has already named the step and a second "Rechtsform" above the list reads
     * as a subheading of itself.
     */
    showTitle = true,

    /** Called after any change, so a caller can recount. */
    onChange = () => {}
  }: { showTitle?: boolean; onChange?: () => void } = $props();

  const codes = LEGAL_FORM_CODES;

  /**
   * Whether the filter is switched on at all.
   *
   * The bar's contract: a checkbox, and the content only once it is ticked.
   * Sixteen forms in two columns is the tallest control in the sidebar, and
   * unfurled by default it pushed everything below it off the screen.
   *
   * Forced on but never off — the same reasoning as TechnologyFilter. Ticking
   * this writes nothing, it reveals a list, so a tick derived from the store
   * would clear itself on the next frame and the list would never open. The
   * store turns it on, which is what a filter pill or the assistant needs; the
   * customer's click is the only thing that turns it off, and that clears.
   */
  let enabled = $state(false);

  $effect(() => {
    if ($legalForm.length > 0 || $excludeLegalForm.length > 0) enabled = true;
  });

  function toggleEnabled() {
    enabled = !enabled;
    if (!enabled) clear();
  }

  const included = $derived(new Set($legalForm));
  const excluded = $derived(new Set($excludeLegalForm));
  const anySet = $derived($legalForm.length > 0 || $excludeLegalForm.length > 0);

  function keepOnly(code: string) {
    if (included.has(code)) {
      legalForm.set($legalForm.filter((c) => c !== code));
    } else {
      legalForm.set([...$legalForm, code]);
      excludeLegalForm.set($excludeLegalForm.filter((c) => c !== code));
    }
    onChange();
  }

  function drop(code: string) {
    if (excluded.has(code)) {
      excludeLegalForm.set($excludeLegalForm.filter((c) => c !== code));
    } else {
      excludeLegalForm.set([...$excludeLegalForm, code]);
      legalForm.set($legalForm.filter((c) => c !== code));
    }
    onChange();
  }

  function clear() {
    legalForm.set([]);
    excludeLegalForm.set([]);
    onChange();
  }
</script>

<div class={enabled ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  {#if showTitle}
    <!--
      The bar's contract, in the shape CapitalFilters and FoundingFilters
      already use. Drawn only with the title, which is the same condition: the
      guided step has been named by ScreenView and has already asked whether to
      filter by legal form at all, so a checkbox there asks it twice.
    -->
    <div class="mb-3">
      <label class="group flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={enabled}
          onclick={toggleEnabled}
          class="mr-2 h-3 w-3 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200"
        />
        <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        <span
          class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700"
        >
          {$_('filters.legalForm.title')}
        </span>
      </label>
    </div>
  {/if}

  {#if enabled || !showTitle}
    <div class="mb-2 flex items-baseline justify-between gap-2">
      <span></span>

      {#if anySet}
        <button
          type="button"
          onclick={clear}
          class="text-2xs font-medium text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline"
        >
          {$_('buttons.clearAll')}
        </button>
      {/if}
    </div>

    <p class="mb-2 text-2xs text-gray-500">{$_('filters.legalForm.listHint')}</p>

    <!--
    The column headings carry the meaning, so each row needs no label of its
    own beyond the form's name. Sticky, because the list is eighteen rows in a
    panel that scrolls and the two ticks are indistinguishable without them.

    The fill is the rail's, not white. It exists only to be opaque — rows have
    to pass behind this row rather than through it — so it has to be whatever
    the surface behind it is, and that stopped being white when the filter
    groups lost their cards.
  -->
    <div
      class="sticky top-0 z-10 grid grid-cols-[1fr_2.6rem_2.6rem] items-end gap-1 border-b border-hairline bg-surface-chrome pb-1"
    >
      <span></span>
      <span class="text-center text-2xs leading-tight text-gray-500">
        {$_('filters.legalForm.include')}
      </span>
      <span class="text-center text-2xs leading-tight text-gray-500">
        {$_('filters.legalForm.exclude')}
      </span>
    </div>

    <div class="max-h-64 overflow-y-auto">
      {#each codes as code (code)}
        {@const name = $_(`filters.legalForm.name.${code}`, { default: code })}
        <div
          class="grid grid-cols-[1fr_2.6rem_2.6rem] items-center gap-1 border-b border-hairline py-1 last:border-b-0"
        >
          <span class="truncate text-xs text-gray-700" title={name}>{name}</span>

          <span class="flex justify-center">
            <input
              type="checkbox"
              checked={included.has(code)}
              onchange={() => keepOnly(code)}
              aria-label="{$_('filters.legalForm.include')}: {name}"
              class="h-3 w-3 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200"
            />
          </span>

          <span class="flex justify-center">
            <input
              type="checkbox"
              checked={excluded.has(code)}
              onchange={() => drop(code)}
              aria-label="{$_('filters.legalForm.exclude')}: {name}"
              class="h-3 w-3 rounded border-gray-300 text-red-600 focus:ring-1 focus:ring-red-200"
            />
          </span>
        </div>
      {/each}
    </div>
  {/if}
</div>
